import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, ArrowLeft, ArrowRight, Flag } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/api";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const COLS = [
  { id: "todo", label: "To Do" },
  { id: "in_progress", label: "In Progress" },
  { id: "done", label: "Done" },
];
const PRIORITY_STYLE = {
  high: "bg-rose-100 text-rose-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-slate-100 text-slate-600",
};

export default function Tasks() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canIssueCard = ["admin", "accountant"].includes(user?.role);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("mine");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assigned_to: "", priority: "medium", due_date: "" });

  const load = useCallback(async () => {
    const { data } = await API.get("/tasks", { params: { filter } });
    setTasks(data);
  }, [filter]);

  useEffect(() => {
    load().catch(() => {});
    API.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [load]);

  const todayStr = new Date().toLocaleDateString("en-CA");

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (form.due_date && form.due_date < todayStr) return toast.error("Due date cannot be in the past");
    try {
      await API.post("/tasks", { ...form, assigned_to: form.assigned_to || user.id });
      setOpen(false);
      setForm({ title: "", description: "", assigned_to: "", priority: "medium", due_date: "" });
      toast.success("Task assigned");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create task");
    }
  };

  const setStatus = async (id, status) => {
    await API.patch(`/tasks/${id}`, { status });
    load();
  };

  const remove = async (id) => {
    await API.delete(`/tasks/${id}`);
    toast.success("Task deleted");
    load();
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Team Tasks</h1>
          <p className="text-sm text-slate-500">Assign work to your staff and track progress</p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setOpen(true)} data-testid="new-task-button">
          <Plus className="mr-1 h-4 w-4" /> New Task
        </Button>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList data-testid="task-filter-tabs">
          <TabsTrigger value="mine" data-testid="filter-mine">Assigned to me</TabsTrigger>
          <TabsTrigger value="created" data-testid="filter-created">Created by me</TabsTrigger>
          {user?.role === "admin" && <TabsTrigger value="all" data-testid="filter-all">All tasks</TabsTrigger>}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="task-board">
        {COLS.map((c) => {
          const items = tasks.filter((t) => t.status === c.id);
          return (
            <div key={c.id} className="rounded-lg bg-slate-100/70 p-3" data-testid={`task-column-${c.id}`}>
              <div className="mb-3 flex items-center justify-between px-1">
                <span className="text-sm font-semibold text-slate-800">{c.label}</span>
                <Badge variant="secondary">{items.length}</Badge>
              </div>
              <div className="space-y-2">
                {items.map((t) => (
                  <div key={t.id} className="rounded-md border border-slate-200 bg-white p-3 shadow-sm" data-testid={`task-card-${t.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-900">{t.title}</div>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${PRIORITY_STYLE[t.priority]}`}>
                        {t.priority}
                      </span>
                    </div>
                    {t.description && <div className="mt-1 text-xs text-slate-500">{t.description}</div>}
                    <div className="mt-2 text-xs text-slate-500">
                      → {t.assigned_to_name || "Unassigned"} · due {t.due_date || "-"}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-400">by {t.created_by_name}</div>
                    {t.completed_late && !t.has_card && (canIssueCard || t.created_by === user?.id) && (
                      <div className="mt-2 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5" data-testid={`late-flag-${t.id}`}>
                        <Flag className="h-3 w-3 text-amber-600" />
                        <span className="text-[11px] font-medium text-amber-700">Completed late</span>
                        {canIssueCard && (
                          <Button variant="outline" size="sm" className="ml-auto h-6 px-2 text-[11px]" onClick={() => navigate(`/performance?late_task=${t.id}`)} data-testid={`issue-card-${t.id}`}>
                            Issue card
                          </Button>
                        )}
                      </div>
                    )}
                    {t.has_card && <div className="mt-2 text-[10px] font-medium text-slate-400" data-testid={`card-issued-${t.id}`}>Card issued for this task</div>}
                    <div className="mt-2 flex items-center gap-1">
                      {c.id !== "todo" && (
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setStatus(t.id, c.id === "done" ? "in_progress" : "todo")} data-testid={`task-back-${t.id}`} aria-label="Move back">
                          <ArrowLeft className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {c.id !== "done" && (
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setStatus(t.id, c.id === "todo" ? "in_progress" : "done")} data-testid={`task-forward-${t.id}`} aria-label="Move forward">
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="ml-auto h-7 w-7" onClick={() => remove(t.id)} data-testid={`task-delete-${t.id}`} aria-label="Delete task">
                        <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                      </Button>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div className="rounded-md border border-dashed border-slate-300 p-2"><EmptyState compact title="No tasks" /></div>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle>New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="task-title-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" data-testid="task-description-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Assign To</Label>
                <Select value={form.assigned_to || "self"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "self" ? "" : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="task-assignee-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Myself</SelectItem>
                    {users.filter((u) => u.id !== user?.id).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low"><span className="flex items-center gap-1"><Flag className="h-3 w-3" /> Low</span></SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="date" min={todayStr} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" data-testid="task-due-input" />
              </div>
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={create} data-testid="create-task-submit">Assign Task</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
